import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import jm, { errorMessage, formatDate, photoUrl, referredBy, toInputDate } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import useStage from '../common/useStage';
import { useMasters } from '../common/MastersContext';
import SearchSelect, { NOT_REFERRED, departmentOptions, jobOptions, referrerOptions } from '../common/SearchSelect';
import AddCandidateForm from './AddCandidateForm';
import DateField, { TODAY } from '../common/DateField';

/** Where a candidate sitting in Applications can go next. */
const NEXT_STATUS = [
  { value: 'Applied', label: 'Applied (stay here)' },
  { value: 'Shortlisted', label: 'Phone Interview' },
  { value: 'hold', label: 'Hold' },
  { value: 'Rejected', label: 'Rejected' },
];

const Applications = ({ onMoved }) => {
  const masters = useMasters();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});

  const stage = useStage(jm.applications, jm.saveApplication, { onMoved });
  const { rows, loading, reload, editing, detail, detailLoading, openEdit, closeEdit, saving, save } = stage;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Seed the dialog once the candidate's record arrives.
  const seed = useCallback(() => {
    if (!detail) return;
    const app = detail.application;

    setForm({
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      dob: toInputDate(app.dob),
      contact_no: app.contact_no || '',
      email: app.email || '',
      gender: app.gender || 'Male',
      marriage_status: app.marriage_status || 'Single',
      address: app.address || '',
      job_id: app.job_id || '',
      department_id: app.department_id || '',
      refered_by: app.refered_by || 0,
      notes: app.notes || '',
      job_status: 'Applied',
    });
  }, [detail]);

  React.useEffect(seed, [seed]);

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.first_name} ${row.last_name || ''}? This cannot be undone.`)) return;

    try {
      await jm.deleteCandidate(row.application_id);
      toast.success('Candidate deleted');
      reload();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} onDelete={remove} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    { key: 'job_status', label: 'Status', search: false, render: (row) => <StatusBadge label={row.job_status || 'Applied'} /> },
    { key: 'created_at', label: 'Apply Date', render: (row) => formatDate(row.created_at) },
  ];

  return (
    <>
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Add New Candidate</h6>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? 'Close' : '+ Add New'}
          </button>
        </div>
        {showAdd && (
          <div className="card-body">
            <AddCandidateForm
              masters={masters}
              onDone={() => { setShowAdd(false); reload(); }}
            />
          </div>
        )}
      </div>

      <CandidateTable
        title="List All Applied Candidates"
        rows={rows}
        columns={columns}
        loading={loading}
        exportName="applied_candidates"
      />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={() => save(form)}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>First Name</label>
                <input className="form-control required-input" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Last Name</label>
                <input className="form-control" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group required-tooltip">
                <label>Date of Birth</label>
                <DateField value={form.dob || ''} onChange={(v) => set('dob', v)} maxDate={TODAY} required />
              </div>
              <div className="col-md-6 form-group required-tooltip">
                <label>Mobile</label>
                <input className="form-control required-input" maxLength={10} value={form.contact_no || ''} onChange={(e) => set('contact_no', e.target.value)} />
              </div>
              <div className="col-md-6 form-group required-tooltip">
                <label>Email</label>
                <input className="form-control required-input" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div className="col-md-3 form-group">
                <label>Gender</label>
                <select className="form-control" value={form.gender || ''} onChange={(e) => set('gender', e.target.value)}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="col-md-3 form-group">
                <label>Marital Status</label>
                <select className="form-control" value={form.marriage_status || ''} onChange={(e) => set('marriage_status', e.target.value)}>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Widowed">Widowed</option>
                  <option value="Divorced">Divorced or Separated</option>
                </select>
              </div>
              <div className="col-md-6 form-group">
                <label>Job Title</label>
                <SearchSelect
                  value={form.job_id}
                  onChange={(v) => set('job_id', v)}
                  options={jobOptions(masters.jobs)}
                  placeholder="Search job title…"
                />
              </div>
              <div className="col-md-6 form-group">
                <label>Department</label>
                <SearchSelect
                  value={form.department_id}
                  onChange={(v) => set('department_id', v)}
                  options={departmentOptions(masters.departments)}
                  placeholder="Search department…"
                />
              </div>
              <div className="col-md-12 form-group required-tooltip">
                <label>Address</label>
                <textarea className="form-control required-input" rows="2" value={form.address || ''} onChange={(e) => set('address', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Referred By</label>
                <SearchSelect
                  value={form.refered_by}
                  onChange={(v) => set('refered_by', v || NOT_REFERRED)}
                  options={referrerOptions(masters.employees)}
                  placeholder="Search employee…"
                  isClearable={false}
                />
              </div>
              <div className="col-md-6 form-group required-tooltip">
                <label>Move To</label>
                <select className="form-control required-input" value={form.job_status || 'Applied'} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-12 form-group">
                <label>Remark</label>
                <textarea className="form-control" rows="2" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>

            {form.job_status !== 'Applied' && (
              <div className="alert alert-info py-2 mb-0">
                Saving moves this candidate to the{' '}
                <strong>{NEXT_STATUS.find((s) => s.value === form.job_status)?.label}</strong> tab.
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default Applications;
